import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollText, RefreshCw, Filter } from "lucide-react";

const ACTION_COLORS = {
  started: "bg-emerald-500/15 text-emerald-400",
  stopped: "bg-amber-500/15 text-amber-400",
  error: "bg-red-500/15 text-red-400",
  risk_block: "bg-red-500/15 text-red-400",
  order_placed: "bg-blue-500/15 text-blue-400",
  depth_order: "bg-indigo-500/15 text-indigo-400",
  completed: "bg-emerald-500/15 text-emerald-400",
};

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchLogs = useCallback(async () => {
    try {
      const r = await api.get("/logs", { params: { limit: 200 } });
      setLogs(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLogs(); const iv = setInterval(fetchLogs, 10000); return () => clearInterval(iv); }, [fetchLogs]);

  const filtered = filter === "all" ? logs : logs.filter((l) => l.action === filter);
  const actions = [...new Set(logs.map((l) => l.action))];

  return (
    <div className="space-y-6" data-testid="logs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Activity Logs</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Real-time bot activity and system events</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-secondary/50" data-testid="log-filter">
              <Filter className="w-3 h-3 mr-2" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-logs-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-2" />Refresh
          </Button>
        </div>
      </div>

      <Card className="bg-card border-border/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 rounded" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <ScrollText className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
              <p className="text-muted-foreground">No activity logs yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border/30">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold bg-secondary/30">
                <div className="col-span-2">Time</div>
                <div className="col-span-2">Action</div>
                <div className="col-span-2">Bot</div>
                <div className="col-span-6">Message</div>
              </div>
              {/* Rows */}
              <div className="max-h-[600px] overflow-y-auto">
                {filtered.map((log) => (
                  <div key={log.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 hover:bg-secondary/20 transition-colors items-center" data-testid={`log-row-${log.id}`}>
                    <div className="col-span-2 font-mono text-[11px] text-muted-foreground tabular-nums">
                      {new Date(log.timestamp).toLocaleString()}
                    </div>
                    <div className="col-span-2">
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${ACTION_COLORS[log.action] || "bg-secondary text-muted-foreground"}`}>
                        {log.action}
                      </span>
                    </div>
                    <div className="col-span-2 text-xs text-muted-foreground truncate">
                      {log.bot_name || log.bot_id?.slice(0, 8) || "--"}
                    </div>
                    <div className="col-span-6 text-xs text-foreground/80 truncate font-mono">
                      {log.message}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
